// File: src/pages/tiles/downstocking_report/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react'; // Added RefreshCw import

const DownstockingReport = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeHeight, setIframeHeight] = useState('calc(100vh-13rem)');
  const iframeRef = useRef(null);
  
  // The base URL and the external URL with cache-busting
  const baseUrl = "/proxy/downstock/downstock.html";
  const externalUrl = `${baseUrl}?refresh=${Date.now()}`; // Added cache-busting by default
  
  // Add resize handler to adjust iframe height when window is resized
  useEffect(() => {
    const handleResize = () => {
      const headerHeight = 64; // Approximate height of the header in pixels
      const windowHeight = window.innerHeight;
      const newHeight = windowHeight - headerHeight - 32; // 32px for margins/padding
      setIframeHeight(`${newHeight}px`);
    };
    
    // Initial calculation
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Set up a timeout to handle case where iframe never triggers load event
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        // Check if iframe actually loaded content despite not triggering load event
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          try {
            // If we can access this without error, content might be loaded
            const iframeHref = iframe.contentWindow.location.href;
            if (iframeHref !== 'about:blank') {
              setIsLoading(false);
            } else {
              setError("Loading timed out. The downstocking report may be unavailable.");
              setIsLoading(false);
            }
          } catch (e) {
            // Cross-origin error means the iframe loaded something, but we can't access it
            setIsLoading(false);
          }
        } else {
          setError("Failed to load the Downstocking Report. Please check if the service is available.");
          setIsLoading(false);
        }
      }
    }, 10000); // 10 second timeout
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [isLoading]);
  
  const handleIframeLoad = () => {
    setIsLoading(false);
    setError(null);
  };
  
  const handleIframeError = () => {
    setError("Failed to load the Downstocking Report. Please check if the service is available.");
    setIsLoading(false);
  };
  
  // Enhanced refresh function with stronger cache-busting
  const handleRefresh = () => {
    setIsLoading(true);
    setError(null);
    
    // This triggers the iframe to reload with stronger cache-busting
    const iframe = iframeRef.current;
    if (iframe) {
      // Force a complete reload by changing the src with a timestamp and cache control
      const timestamp = Date.now();
      iframe.src = `${baseUrl}?refresh=${timestamp}&nocache=true`;
      
      // For additional cache busting, try to manipulate the iframe content
      try {
        // Attempt to clear any browser cache for the iframe
        if (iframe.contentWindow) {
          iframe.contentWindow.location.reload(true); // true forces reload from server
        }
      } catch (e) {
        // Ignore cross-origin errors - the src change will still work
      }
    }
  };

  return (
    <div className="flex-1 bg-slate-800 border-x border-b border-slate-700 rounded-lg overflow-hidden relative">
      {/* Add a refresh button at the top right corner */}
      <div className="absolute top-2 right-2 z-20">
        <button
          onClick={handleRefresh}
          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-md text-slate-200 flex items-center gap-1"
          title="Force refresh content"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="text-xs">Refresh</span>
        </button>
      </div>
      
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800 bg-opacity-80 z-10">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-slate-200">Loading Downstocking Report...</span>
          </div>
        </div>
      )}
      
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-6 z-10">
          <div className="bg-red-900/30 border border-red-800 text-red-300 px-6 py-4 rounded-md max-w-md text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p className="mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-red-800 hover:bg-red-700 rounded-md text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      
      <iframe
        ref={iframeRef}
        id="downstock-iframe"
        src={externalUrl} // This now includes cache-busting by default
        title="Downstocking Report"
        className="w-full"
        style={{ 
          height: iframeHeight,
          display: error ? 'none' : 'block',
          border: 'none' 
        }}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
        loading="eager"
      ></iframe>
    </div>
  );
};

export default DownstockingReport;
